import { Amplify, Auth } from 'aws-amplify';
import config from './config';

// Determine redirect URLs based on current location
const getRedirectUrl = (path: string) => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    return `${origin}${path}`;
  }
  // Fallback for SSR or build time
  return `http://localhost:5173${path}`;
};

let isConfigured = false;

export const configureAmplify = () => {
  if (isConfigured) {
    return;
  }

  // Check if we have required config values
  if (!config.USER_POOL_ID || !config.USER_POOL_CLIENT_ID) {
    console.error('Missing required auth configuration');
    return;
  }

  const authConfig = {
    Auth: {
      region: config.AWS_REGION,
      userPoolId: config.USER_POOL_ID,
      userPoolWebClientId: config.USER_POOL_CLIENT_ID,
      mandatorySignIn: true,
      authenticationFlowType: 'USER_SRP_AUTH',
      oauth: {
        domain: config.USER_POOL_DOMAIN.replace('https://', ''),
        scope: ['email', 'profile', 'openid'],
        redirectSignIn: getRedirectUrl('/dashboard'),
        redirectSignOut: getRedirectUrl('/logout'),
        responseType: 'code',
      },
    },
  };

  console.log('🔧 Configuring Amplify with:', authConfig);
  Amplify.configure(authConfig);
  isConfigured = true;
};

// Configure on import if config is available
if (config.USER_POOL_ID && config.USER_POOL_CLIENT_ID) {
  configureAmplify();
}

export const authService = {
  async signIn(email: string, password: string) {
    try {
      const user = await Auth.signIn(email, password);
      return { success: true, user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async signUp(email: string, password: string, name?: string) {
    try {
      const result = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          ...(name && { name }),
        },
      });
      return { success: true, result };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async confirmSignUp(email: string, code: string) {
    try {
      await Auth.confirmSignUp(email, code);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async resendConfirmationCode(email: string) {
    try {
      await Auth.resendSignUp(email);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async signOut() {
    try {
      await Auth.signOut();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async getCurrentUser() {
    try {
      const user = await Auth.currentAuthenticatedUser();
      return { success: true, user };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async forgotPassword(email: string) {
    try {
      await Auth.forgotPassword(email);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async forgotPasswordSubmit(email: string, code: string, newPassword: string) {
    try {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  async changePassword(oldPassword: string, newPassword: string) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, oldPassword, newPassword);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Naming the provider is what keeps the Cognito Hosted UI from rendering.
  // Called bare, federatedSignIn() sends the browser to the Hosted UI's own
  // login page, which — because this app client's SupportedIdentityProviders is
  // ["COGNITO", "Discord"] — draws a second email/password form AND a Discord
  // button, so the user picks Discord twice. Passing the provider adds
  // identity_provider=Discord to the authorize URL and Cognito 302s straight
  // through to Discord without drawing anything.
  //
  // `customProvider`, not `provider`: Discord is a custom OIDC provider in the
  // pool (via the shim in stacks/auth), not one of Amplify's built-in socials.
  async federatedSignIn(customProvider: string = 'Discord') {
    try {
      await Auth.federatedSignIn({ customProvider });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },
};

export { Auth };
export default authService;